'use client';

import React, { useRef, useState, useEffect } from 'react';
import { Button, ImageCard } from '@ui';
import { type CarouselItem } from './CarouselData';
import { prefersReducedMotion } from '@utils/prefersReducedMotion';
import { IconChevronLeft, IconChevronRight } from '@icons';

interface CarouselProps {
  items: CarouselItem[];
  locale?: 'en' | 'es';
}

const Carousel: React.FC<CarouselProps> = ({ items, locale = 'en' }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollButtons = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    setCanScrollPrev(viewport.scrollLeft > 0);
    setCanScrollNext(viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 1);
  };

  const scroll = (dir: 'prev' | 'next') => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const card = viewport.querySelector('.carousel-item') as HTMLElement;
    if (!card) return;
    const scrollAmount = card.offsetWidth + 16; // 16px gap
    viewport.scrollBy({
      left: dir === 'next' ? scrollAmount : -scrollAmount,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
    // Wait for scroll to finish, then update
    setTimeout(updateScrollButtons, 350);
  };

  useEffect(() => {
    updateScrollButtons();
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      viewport.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [items.length]);

  return (
    <div className='carousel' data-carousel role='region' aria-label='Example carousel'>
      <Button
        className='carousel-btn prev'
        aria-label='Scroll left'
        onClick={() => scroll('prev')}
        as='button'
        type='button'
        disabled={!canScrollPrev}
        icon={<IconChevronLeft />}
      />
      <Button
        className='carousel-btn next'
        aria-label='Scroll right'
        onClick={() => scroll('next')}
        as='button'
        type='button'
        disabled={!canScrollNext}
        icon={<IconChevronRight />}
      />
      <div
        className='carousel-viewport'
        ref={viewportRef}
        tabIndex={0}
        aria-roledescription='carousel'
        aria-label='Cards'
        style={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          gap: 16,
        }}
      >
        {items.map((item, i) => (
          <ImageCard
            key={i}
            className='carousel-item'
            aria-label={`Card ${i + 1}`}
            style={{ minWidth: 220, scrollSnapAlign: 'start' }}
            carousel-item={item}
            carousel-index={i + 1}
            locale={locale}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
